import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TwoFactorAuth = ({ onVerify, onResendOTP, loading, error, email }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleOtpChange = (index, value) => {
    if (value?.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs?.current?.[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (newOtp?.every(digit => digit !== '') && newOtp?.join('')?.length === 6) {
      onVerify(newOtp?.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !otp?.[index] && index > 0) {
      inputRefs?.current?.[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pastedData = e?.clipboardData?.getData('text')?.slice(0, 6);
    const newOtp = pastedData?.split('')?.concat(Array(6)?.fill(''))?.slice(0, 6);
    setOtp(newOtp);
    
    if (pastedData?.length === 6) {
      onVerify(pastedData);
    }
  };

  const handleResend = () => {
    setTimeLeft(300);
    setCanResend(false);
    setOtp(['', '', '', '', '', '']);
    onResendOTP();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const maskedEmail = email ? email?.replace(/(.{2})(.*)(@.*)/, '$1***$3') : '';

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="flex items-center justify-center w-16 h-16 bg-accent/20 rounded-full mx-auto mb-4">
          <Icon name="Smartphone" size={32} className="text-accent" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Two-Factor Authentication</h2>
        <p className="text-muted-foreground text-sm">
          Enter the 6-digit code sent to {maskedEmail}
        </p>
      </div>
      {error && (
        <div className="bg-error/10 border border-error rounded-lg p-4 flex items-start space-x-3">
          <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-error font-medium">Verification Failed</p>
            <p className="text-error/80 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}
      <div className="space-y-4">
        <div className="flex justify-center space-x-3">
          {otp?.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e?.target?.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={loading}
              className="w-12 h-12 text-center text-lg font-semibold bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent security-transition disabled:opacity-50"
            />
          ))}
        </div>

        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-2">
            Time remaining: <span className="font-mono text-accent">{formatTime(timeLeft)}</span>
          </p>
          
          {canResend ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResend}
              iconName="RefreshCw"
              iconPosition="left"
              className="text-accent hover:text-accent/80"
            >
              Resend Code
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">
              Didn't receive the code? You can resend in {formatTime(timeLeft)}
            </p>
          )}
        </div>
      </div>
      <Button
        variant="default"
        size="lg"
        fullWidth
        loading={loading}
        iconName="Shield"
        iconPosition="left"
        onClick={() => onVerify(otp?.join(''))}
        disabled={otp?.some(digit => digit === '') || loading}
        className="mt-6"
      >
        {loading ? 'Verifying...' : 'Verify & Continue'}
      </Button>
      <div className="text-center">
        <button
          onClick={() => window.location?.reload()}
          className="text-sm text-muted-foreground hover:text-foreground security-transition"
        >
          ← Back to login
        </button>
      </div>
    </div>
  );
};

export default TwoFactorAuth;